import * as XLSX from "xlsx";

export type WorkbookCellKind =
  | "blank"
  | "text"
  | "number"
  | "boolean"
  | "formula"
  | "error";

export interface WorkbookCell {
  kind: WorkbookCellKind;
  value: string | number | boolean | null;
}

export interface WorkbookSheet {
  name: string;
  rows: WorkbookCell[][];
  merged_ranges: string[];
}

export interface StudentTimetableWorkbook {
  sheets: WorkbookSheet[];
}

function convertedCell(cell: XLSX.CellObject | undefined): WorkbookCell {
  if (!cell) return { kind: "blank", value: null };
  if (cell.f) return { kind: "formula", value: cell.f };
  if (cell.t === "s") {
    return { kind: "text", value: typeof cell.v === "string" ? cell.v : String(cell.v ?? "") };
  }
  if (cell.t === "n" || cell.t === "d") {
    return { kind: "number", value: typeof cell.v === "number" ? cell.v : Number(cell.v) };
  }
  if (cell.t === "b") return { kind: "boolean", value: Boolean(cell.v) };
  if (cell.t === "e") return { kind: "error", value: String(cell.w ?? cell.v ?? "") };
  return { kind: "blank", value: null };
}

export async function parseStudentTimetableFile(
  file: File
): Promise<StudentTimetableWorkbook> {
  if (!file.name.toLocaleLowerCase().endsWith(".xlsx")) {
    throw new Error("只接受 .xlsx 文件，请使用模板另存后再导入");
  }
  const data = await file.arrayBuffer();
  let source: XLSX.WorkBook;
  try {
    source = XLSX.read(data, { type: "array", raw: true, cellFormula: true });
  } catch {
    throw new Error("无法读取该 Excel 文件，请确认文件没有损坏");
  }
  return {
    sheets: source.SheetNames.map((name) => {
      const worksheet = source.Sheets[name];
      const range = worksheet["!ref"]
        ? XLSX.utils.decode_range(worksheet["!ref"])
        : { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };
      const rows: WorkbookCell[][] = [];
      for (let row = 0; row <= range.e.r; row += 1) {
        const cells: WorkbookCell[] = [];
        for (let column = 0; column <= range.e.c; column += 1) {
          cells.push(convertedCell(worksheet[XLSX.utils.encode_cell({ r: row, c: column })]));
        }
        rows.push(cells);
      }
      return {
        name,
        rows,
        merged_ranges: (worksheet["!merges"] ?? []).map((merge) => XLSX.utils.encode_range(merge))
      };
    })
  };
}

export function periodOptions(workbook: StudentTimetableWorkbook): Array<{
  periodNumber: number;
  label: string;
}> {
  const first = workbook.sheets[0];
  if (!first) return [];
  return first.rows.slice(1).flatMap((row): Array<{ periodNumber: number; label: string }> => {
    const value = row[0]?.value;
    if (typeof value !== "string") return [];
    const match = /^第([1-9]\d*)节\s+/.exec(value.trim());
    return match ? [{ periodNumber: Number(match[1]), label: value }] : [];
  });
}

export function downloadStudentTimetableTemplate(): void {
  const headers = ["节次/时间", "星期一", "星期二", "星期三", "星期四", "星期五"];
  const rows = [
    ["第1节 08:00-08:40", "语文", "数学", "语文", "音乐", "数学"],
    ["第2节 08:50-09:30", "数学", "语文", "美术", "语文", "体育"],
    ["第3节 10:00-10:40", "音乐", "体育", "数学", "数学", "美术"],
    ["第4节 13:30-14:10", "美术", "音乐", "体育", "美术", "音乐"]
  ];
  const workbook = XLSX.utils.book_new();
  for (const name of ["张晨-三年级2班", "李然-四年级1班"]) {
    const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    sheet["!cols"] = [{ wch: 24 }, ...Array.from({ length: 5 }, () => ({ wch: 14 }))];
    XLSX.utils.book_append_sheet(workbook, sheet, name);
  }
  XLSX.writeFile(workbook, "学生原班课表导入模板.xlsx");
}
